import * as React from 'react'
import { mtc } from '../../../utils/style-helpers'
import { badgeStyles } from './styles'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../icon'

export type BadgeColor =
  | 'green'
  | 'olive'
  | 'orange'
  | 'red'
  | 'yellow'
  | 'purple'
  | 'blue'
  | 'pink'
  | 'gray'
  | 'white'

export const BADGE_COLOR_ARRAY: BadgeColor[] = [
  'green',
  'olive',
  'orange',
  'red',
  'yellow',
  'purple',
  'blue',
  'pink',
  'gray',
  'white',
]

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Set the variant of the badge to one of: */
  variant?: 'default' | 'secondary'
  /** Set the color of the badge to one of: */
  color?: BadgeColor
  /** Set the icon of the badge to one of: */
  icon?: IconDefinition
  /** Set the size of the badge to one of: */
  size?: 'sm' | 'md' | 'lg'
}

// Define a mapping from BadgeColor to Icon color values for the 'secondary' variant
const secondaryBadgeColorMap: Record<BadgeColor, string> = {
  green: 'text-green-200',
  olive: 'text-moss-200',
  orange: 'text-orange-200',
  red: 'text-red-200',
  yellow: 'text-yellow-200',
  purple: 'text-purple-200',
  blue: 'text-blue-200',
  pink: 'text-pink-200',
  gray: 'text-neutral-200',
  white: 'text-neutral-0',
}

export const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  (
    {
      variant = 'default',
      color = 'green',
      icon,
      className,
      size = 'md',
      ...props
    },
    ref
  ) => {
    // Determine the icon color based on the variant
    const iconColorClass =
      variant === 'default' ? 'text-neutral-0' : secondaryBadgeColorMap[color]
    return (
      <div
        ref={ref}
        className={mtc(badgeStyles({ variant, color, size }), className)}
        {...props}
      >
        {icon && <Icon icon={icon} color={iconColorClass} className="mr-2" />}
        {props.children}
      </div>
    )
  }
)

Badge.displayName = 'Badge'
